import { createEmptyCollection, setItemInCollection, getItemFromCollection, removeByKeyFromCollection } from 'helpers/normalizeHelpers'
import { isRequestAction, isFailAction, isSuccessAction } from 'helpers/actionsHelpers'
import { ComposesState, ComposeId, ComposeType, COMPOSE_DEFAULT_INPUT, ComposeState } from 'interfaces/feed'
import {
  COMPOSE_SUBMIT,
  FEED_COMPOSE_FETCH_LINK,
  FEED_COMPOSE_IMAGE_REMOVE,
  FEED_COMPOSE_CLEAN_UP,
  FEED_COMPOSE_FETCH_LINK_REMOVE,
  FEED_COMPOSE_TYPE_TEXT,
  COMPOSE_MOUNT_WITH_POST,
  COMPOSE_MOUNT_WITH_COMMENT,
  COMPOSE_FOCUS,
  COMPOSE_MOUNT,
  COMPOSE_UNMOUNT,
  COMPOSE_BLUR,
  FEED_COMPOSE_SELECT_FILTER,
  FEED_COMPOSE_ADD_IMAGE_ATTEMPT,
  FEED_COMPOSE_MENTION,
  COMPOSE_EXEC_COMMAND,
  COMPOSE_SET_INPUT_BEHAVIOR,
  COMPOSE_MOUNT_WITH_REPLY,
  COMPOSE_VALIDATE,
  COMPOSE_EXISTS,
} from 'actions/feedComposeActions'
import { Reducer } from 'interfaces'
import { FEED_OWN_POST_COMPOSE_ID } from 'constants/feed'
import * as findIndex from 'lodash/findIndex'
import { composeHasContentCombiner } from 'selectors/feedComposeSelectors'
import { isSocialInsightPost } from 'helpers/feedHelpers'

const COMPOSE_LAST_FOCUS_INITIAL_VALUE = FEED_OWN_POST_COMPOSE_ID
export const composeLastFocus: Reducer<ComposeId> = (prevState = COMPOSE_LAST_FOCUS_INITIAL_VALUE, action) => {
  switch (action.type) {
    case COMPOSE_FOCUS: {
      const { composeId } = action.payload
      return composeId
    }

    case COMPOSE_UNMOUNT: {
      return COMPOSE_LAST_FOCUS_INITIAL_VALUE
    }
  }
  return prevState
}

export const getComposeInitialState = (id: ComposeId, type: ComposeType): ComposeState => ({
  isSubmitting: false,
  isSubmitted: false,
  errorCode: null,
  errorExtra: null,
  link: null,
  isLinkLoading: false,
  isLinkRemoved: false,
  imagesIds: [],
  mentions: [],
  id,
  text: '',
  isExists: false,
  type,
  isFocused: false,
  isEdit: false,
  execCommand: null,
  inputBehavior: COMPOSE_DEFAULT_INPUT,
})

export const composes: Reducer<ComposesState> = (prevState = createEmptyCollection(), action) => {
  switch (action.type) {
    case COMPOSE_EXEC_COMMAND: {
      const { composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      return setItemInCollection<ComposeState, 'id'>(prevState, {
        ...prevCompose,
        execCommand: action.payload.command,
      }, 'id')
    }

    case COMPOSE_SET_INPUT_BEHAVIOR: {
      const { composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevCompose,
        inputBehavior: action.payload.type,
      }, 'id')
    }

    case COMPOSE_FOCUS: {
      const { composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevCompose,
        isFocused: true,
      }, 'id')
    }

    case COMPOSE_BLUR: {
      const { composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevCompose,
        isFocused: false,
      }, 'id')
    }

    case COMPOSE_MOUNT: {
      const { composeId, type, postUid, placeholder, focusOnMount, isOpportunityReview } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId) || getComposeInitialState(composeId, type)
      return setItemInCollection<ComposeState, 'id'>(prevState, {
        ...prevCompose,
        postUid,
        placeholder,
        focusOnMount,
        isOpportunityReview,
      }, 'id')
    }

    case COMPOSE_UNMOUNT: {
      const { composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      const { imagesIds, link, isLinkRemoved, text } = prevCompose as ComposeState
      const hasContent = composeHasContentCombiner(!!imagesIds.length, !!(link && !isLinkRemoved), !!text)
      if (!hasContent) {
        return removeByKeyFromCollection<ComposeState, 'id'>(prevState, composeId)
      }

      return setItemInCollection(prevState, {
        ...prevCompose,
        isExists: false,
      }, 'id')
    }

    case FEED_COMPOSE_CLEAN_UP: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { composeId } = action.payload.params
      return removeByKeyFromCollection<ComposeState, 'id'>(prevState, composeId)
    }

    case FEED_COMPOSE_TYPE_TEXT: {
      const { composeId, text } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevCompose,
        text,
      }, 'id')
    }

    case COMPOSE_VALIDATE: {
      const { composeId } = action.payload.params
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      if (isRequestAction(action)) {
        return setItemInCollection<ComposeState, 'id'>(prevState, {
          ...prevCompose,
          errorCode: null,
          errorExtra: null,
        }, 'id')
      }

      if (isFailAction(action)) {
        const { errorCode, errorExtra = null } = action.payload
        return setItemInCollection<ComposeState, 'id'>(prevState, {
          ...prevCompose,
          errorCode,
          errorExtra,
        }, 'id')
      }

      return prevState
    }

    case COMPOSE_SUBMIT: {
      const { composeId } = action.payload.params
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      if (isRequestAction(action)) {
        return setItemInCollection<ComposeState, 'id'>(prevState, {
          ...prevCompose,
          isSubmitting: true,
          errorCode: null,
        }, 'id')
      }

      if (isFailAction(action)) {
        const { errorCode, errorExtra = null } = action.payload
        return setItemInCollection<ComposeState, 'id'>(prevState, {
          ...prevCompose,
          isSubmitting: false,
          errorCode,
          errorExtra,
        }, 'id')
      }

      return setItemInCollection(prevState, {
        ...prevCompose,
        isSubmitting: false,
        isSubmitted: true,
      }, 'id')
    }

    case FEED_COMPOSE_FETCH_LINK: {
      const { composeId } = action.payload.params
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)

      if (!prevCompose) {
        return prevState
      }

      if (isRequestAction(action)) {
        return setItemInCollection<ComposeState, 'id'>(prevState, {
          ...prevCompose,
          isLinkLoading: true,
        }, 'id')
      }

      if (isFailAction(action)) {
        return setItemInCollection(prevState, {
          ...prevCompose,
          isLinkLoading: false,
        }, 'id')
      }

      // do nothing with 'other' type and type 'image' will be handled like copy pasted images in feedEpic
      if (action.payload.response.type !== 'open_graph') {
        return setItemInCollection(prevState, {
          ...prevCompose,
          isLinkLoading: false,
        }, 'id')
      }

      return setItemInCollection<ComposeState, 'id'>(prevState, {
        ...prevCompose,
        isLinkLoading: false,
        isLinkRemoved: false,
        link: action.payload.response,
      }, 'id')
    }

    case FEED_COMPOSE_FETCH_LINK_REMOVE: {
      const { composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)

      if (!prevCompose) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevCompose,
        isLinkRemoved: true,
      }, 'id')
    }

    case FEED_COMPOSE_IMAGE_REMOVE: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { composeId, id } = action.payload.params
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      const { imagesIds } = prevCompose
      const imageIndex = imagesIds.indexOf(id)
      if (imageIndex === -1) {
        return prevState
      }

      const nextImagesIds = [...imagesIds]
      nextImagesIds.splice(imageIndex, 1)
      return setItemInCollection(prevState, {
        ...prevCompose,
        imagesIds: nextImagesIds,
      }, 'id')
    }

    case COMPOSE_MOUNT_WITH_POST: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const {
        params: {
          composeId,
          postUid,
          placeholder,
          focusOnMount,
        },
        response: {
          post,
        },
      } = action.payload

      if (isSocialInsightPost(post)) {
        return prevState
      }

      const { imagesIds, links: postLinks } = post
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId) || getComposeInitialState(composeId, 'post')
      return setItemInCollection<ComposeState, 'id'>(prevState, {
        ...prevCompose,
        text: post.text,
        imagesIds,
        mentions: post.mentions,
        postUid,
        link: postLinks && postLinks.length ? postLinks[0] : null,
        isEdit: true,
        placeholder,
        focusOnMount,
      }, 'id')
    }

    case COMPOSE_MOUNT_WITH_COMMENT: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const {
        params: {
          composeId,
          postUid,
          commentUid,
          placeholder,
          focusOnMount,
          isOpportunityReview,
        },
        response: {
          comment,
        },
      } = action.payload

      const { imagesIds, links: commentLinks } = comment
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId) || getComposeInitialState(composeId, 'comment')
      return setItemInCollection<ComposeState, 'id'>(prevState, {
        ...prevCompose,
        text: comment.text,
        imagesIds,
        mentions: comment.mentions,
        postUid,
        commentUid,
        link: commentLinks && commentLinks.length ? commentLinks[0] : null,
        isEdit: true,
        placeholder,
        focusOnMount,
        isOpportunityReview,
      }, 'id')
    }

    case COMPOSE_MOUNT_WITH_REPLY: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const {
        params: {
          postUid,
          composeId,
          parentCommentUid,
          replyCommentUid,
          placeholder,
          focusOnMount,
        },
        response: {
          text = '',
          mention = null,
        },
      } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId) || getComposeInitialState(composeId, 'comment')

      return setItemInCollection<ComposeState, 'id'>(prevState, {
        ...prevCompose,
        text,
        mentions: [
          ...prevCompose.mentions,
          ...mention ? [mention] : [],
        ],
        postUid,
        parentCommentUid,
        replyCommentUid,
        placeholder,
        focusOnMount,
      }, 'id')
    }

    case COMPOSE_EXISTS: {
      const { composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevCompose,
        isExists: true,
      }, 'id')
    }

    case FEED_COMPOSE_SELECT_FILTER: {
      const { filter, composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      return setItemInCollection<ComposeState, 'id'>(prevState, {
        ...prevCompose,
        filter,
      }, 'id')
    }

    case FEED_COMPOSE_ADD_IMAGE_ATTEMPT: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { payload } = action
      const { composeId } = payload.params
      const { imageId } = payload.response
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      const { imagesIds } = prevCompose
      const nextImagesIds = [
        ...imagesIds,
        imageId,
      ]

      return setItemInCollection(prevState, {
        ...prevCompose,
        imagesIds: nextImagesIds,
      }, 'id')
    }

    case FEED_COMPOSE_MENTION: {
      const { mention, uid, composeId } = action.payload
      const prevCompose = getItemFromCollection<ComposeState, 'id'>(prevState, composeId)
      if (!prevCompose) {
        return prevState
      }

      const { mentions } = prevCompose
      const nextMentions = [...mentions]
      const index = findIndex(mentions, { uid })
      const insertIndex = index !== -1 ? index : mentions.length
      nextMentions.splice(insertIndex, 1, { username: mention, uid })

      return setItemInCollection(prevState, {
        ...prevCompose,
        mentions: nextMentions,
      }, 'id')
    }
  }
  return prevState
}
